import React, { useMemo, useState } from "react";
import { ExternalLink, HelpCircle, Loader2, UserPlus } from "lucide-react";
import { Dialog } from "@/components/Dialog";
import { HelpTooltip } from "@/components/HelpTooltip";
import { TabSelector } from "@/components/TabSelector";
import {
  getBinanceLeadDetailsUrl,
  type CopyTradeLeaderSnapshot,
} from "@/api/copy-trade";
import {
  useCopyTradeLeaderDetail,
  useCopyTradeLeaderPositions,
} from "@/hooks/useCopyTrade";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import type {
  CopyTradeLeadOpenPosition,
  CopyTradeLeaderPosition,
} from "@crypto-trading/common/browser";

interface CopyTradeLeaderDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadPortfolioId: string;
  nickname: string;
  /** Browse/subscribed snapshot — supplies ROI, MDD, win rate when detail API lacks them */
  snapshot?: CopyTradeLeaderSnapshot;
  isSubscribed?: boolean;
  onSubscribe?: () => void;
}

type PositionsTab = "open" | "history";

const METRIC_TOOLTIPS = {
  roi: "Return on investment over the last 30 days, as reported by Binance for this lead portfolio.",
  mdd: "Maximum Drawdown — the largest peak-to-trough drop in portfolio value over the lookback window. Higher MDD means deeper historical losses from a prior high.",
  winRate:
    "Share of closed trades that ended profitable, based on Binance's copy trading stats.",
  aum: "Assets Under Management — total value of funds allocated to this lead trader's copy trading portfolio on Binance.",
  sharpe:
    "Sharpe ratio — risk-adjusted return. Higher is better: above 1 is generally strong; below 0 means returns did not justify the volatility taken.",
  marginBalance:
    "The lead trader's current margin balance on Binance. This reflects their account, not yours.",
  copierPnl:
    "Combined profit or loss of all copiers on Binance following this trader. Shown for context only — your Trigger copies use your own sizing and exchanges.",
} as const;

const isFiniteNumber = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value);

const formatUsd = (value: number | null | undefined) =>
  isFiniteNumber(value)
    ? value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";

const formatPercent = (value: number | null | undefined) =>
  isFiniteNumber(value) ? `${value.toFixed(2)}%` : "—";

const formatDecimal = (value: number | null | undefined, decimals = 2) =>
  isFiniteNumber(value) ? value.toFixed(decimals) : "—";

const formatDate = (timestamp?: number) => {
  if (!timestamp) return "—";
  return new Date(timestamp).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (timestamp?: number | null) => {
  if (!timestamp) return "—";
  return new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const pnlColor = (value: number | null | undefined) => {
  if (!isFiniteNumber(value) || value === 0) return "text-text-primary";
  return value > 0 ? "text-green-400" : "text-red-400";
};

export const CopyTradeLeaderDetailsDialog: React.FC<
  CopyTradeLeaderDetailsDialogProps
> = ({ open, onOpenChange, leadPortfolioId, nickname, snapshot, isSubscribed, onSubscribe }) => {
  const [positionsTab, setPositionsTab] = useState<PositionsTab>("open");

  const {
    data: detail,
    isLoading,
    isError,
  } = useCopyTradeLeaderDetail(
    leadPortfolioId,
    "binance",
    open && !!leadPortfolioId
  );

  const {
    data: positions,
    isLoading: positionsLoading,
    isError: positionsError,
  } = useCopyTradeLeaderPositions(
    leadPortfolioId,
    "binance",
    open && !!leadPortfolioId
  );

  const displayName = detail?.nickname || nickname;
  const binanceUrl = getBinanceLeadDetailsUrl(leadPortfolioId);

  const sharpRatio = detail?.sharpRatio ?? snapshot?.sharpRatio;
  const roi = snapshot?.roi;
  const mdd = snapshot?.mdd;
  const winRate = snapshot?.winRate;

  const openPositions = positions?.open ?? [];
  const historyPositions = positions?.history ?? [];

  const handleOpenExternal = () => {
    window.open(binanceUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={displayName}
      maxWidth="2xl"
      contentClassName="p-0"
      footer={
        <div className="flex justify-end gap-3">
          {onSubscribe && (
            <button
              type="button"
              onClick={onSubscribe}
              disabled={isSubscribed}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-accent text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="w-4 h-4" />
              {isSubscribed ? "Subscribed" : "Subscribe"}
            </button>
          )}
          <button
            type="button"
            onClick={handleOpenExternal}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-text-primary font-medium text-sm hover:bg-tertiary transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Open in Binance
          </button>
        </div>
      }
    >
      {isLoading && (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      )}

      {isError && (
        <div className="p-6 text-center space-y-4">
          <p className="text-text-secondary">
            Could not load trader details. You can still view the full profile
            on Binance.
          </p>
          <button
            type="button"
            onClick={handleOpenExternal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-accent text-white font-medium"
          >
            <ExternalLink className="w-4 h-4" />
            Open in Binance
          </button>
        </div>
      )}

      {detail && !isLoading && (
        <div className="p-6 space-y-6">
          <div className="flex items-start gap-4">
            {detail.avatarUrl ? (
              <img
                src={detail.avatarUrl}
                alt={detail.nickname}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-tertiary flex items-center justify-center text-xl font-bold">
                {detail.nickname.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-text-primary">
                  {detail.nickname}
                </h3>
                <span className="px-2 py-0.5 rounded-full text-xs bg-black/40 capitalize">
                  {detail.status.toLowerCase()}
                </span>
              </div>
              <p className="text-sm text-text-secondary mt-1">
                {detail.favoriteCount.toLocaleString()} favorites
              </p>
              {detail.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {detail.tags.map((tag) => (
                    <span
                      key={tag.tagName}
                      className="px-2 py-0.5 rounded-full text-xs bg-accent/10 text-accent"
                    >
                      {tag.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {detail.description && (
            <p className="text-sm text-text-secondary whitespace-pre-line leading-relaxed">
              {detail.description}
            </p>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {roi !== undefined && (
              <Stat
                label="ROI (30D)"
                value={formatPercent(roi)}
                tooltip={METRIC_TOOLTIPS.roi}
              />
            )}
            {mdd !== undefined && (
              <Stat
                label="MDD"
                value={formatPercent(mdd)}
                tooltip={METRIC_TOOLTIPS.mdd}
              />
            )}
            {winRate !== undefined && (
              <Stat
                label="Win rate"
                value={formatPercent(winRate)}
                tooltip={METRIC_TOOLTIPS.winRate}
              />
            )}
            <Stat
              label="AUM"
              value={`$${formatUsd(detail.aumAmount)}`}
              tooltip={METRIC_TOOLTIPS.aum}
            />
            <Stat
              label="Sharpe ratio"
              value={formatDecimal(sharpRatio)}
              tooltip={METRIC_TOOLTIPS.sharpe}
            />
            <Stat
              label="Margin balance"
              value={`$${formatUsd(detail.marginBalance)}`}
              tooltip={METRIC_TOOLTIPS.marginBalance}
            />
            <Stat
              label="Copier PnL (Binance)"
              value={`$${formatUsd(detail.copierPnl)} ${detail.copierPnlAsset}`}
              tooltip={METRIC_TOOLTIPS.copierPnl}
            />
            <Stat
              label="Total copiers"
              value={detail.totalCopyCount.toLocaleString()}
            />
            <Stat label="Started" value={formatDate(detail.startTime)} />
            <Stat label="Last trade" value={formatDate(detail.lastTradeTime)} />
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-text-primary">
              Positions
            </h4>
            <TabSelector<PositionsTab>
              variant="compact"
              options={[
                {
                  value: "open",
                  label: `Open (${openPositions.length})`,
                },
                {
                  value: "history",
                  label: `History (${historyPositions.length})`,
                },
              ]}
              value={positionsTab}
              onChange={setPositionsTab}
            />

            {positionsLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
              </div>
            )}

            {positionsError && !positionsLoading && (
              <p className="text-sm text-text-secondary text-center py-6">
                Could not load positions for this trader.
              </p>
            )}

            {!positionsLoading && !positionsError && positionsTab === "open" && (
              openPositions.length === 0 ? (
                <p className="text-sm text-text-secondary text-center py-6">
                  No open positions right now.
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {openPositions.map((pos) => (
                    <OpenPositionRow
                      key={pos.id}
                      position={pos}
                      livePriceEnabled={open}
                    />
                  ))}
                </div>
              )
            )}

            {!positionsLoading &&
              !positionsError &&
              positionsTab === "history" && (
                historyPositions.length === 0 ? (
                  <p className="text-sm text-text-secondary text-center py-6">
                    No position history available.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {historyPositions.map((pos) => (
                      <HistoryPositionRow key={pos.id} position={pos} />
                    ))}
                  </div>
                )
              )}
          </div>
        </div>
      )}
    </Dialog>
  );
};

const SideBadge: React.FC<{ side: "Long" | "Short" }> = ({ side }) => (
  <span
    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
      side === "Long"
        ? "bg-green-500/15 text-green-400"
        : "bg-red-500/15 text-red-400"
    }`}
  >
    {side}
  </span>
);

const OpenPositionRow: React.FC<{
  position: CopyTradeLeadOpenPosition;
  livePriceEnabled: boolean;
}> = ({ position, livePriceEnabled }) => {
  const { price: liveMarkPrice, isSubscribed } = useTokenLivePrice(
    position.symbol,
    "binance",
    livePriceEnabled
  );

  const displayMarkPrice = liveMarkPrice ?? position.markPrice;
  const livePnl = useMemo(() => {
    if (!displayMarkPrice || !position.entryPrice) {
      return position.unrealizedProfit;
    }
    const delta = displayMarkPrice - position.entryPrice;
    return position.side === "Long"
      ? position.positionAmount * delta
      : position.positionAmount * -delta;
  }, [
    displayMarkPrice,
    position.entryPrice,
    position.positionAmount,
    position.side,
    position.unrealizedProfit,
  ]);

  return (
    <div className="bg-primary/30 rounded-xl p-3 border border-border/50 flex items-center justify-between gap-3">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-text-primary">
            {position.symbol}
          </span>
          <SideBadge side={position.side} />
          <span className="text-xs text-text-secondary">
            {position.leverage}x
          </span>
        </div>
        <p className="text-xs text-text-secondary flex items-center gap-1 flex-wrap">
          <span>Entry ${formatDecimal(position.entryPrice, 4)}</span>
          <span>·</span>
          <span
            className={isSubscribed ? "transition-all duration-150" : undefined}
          >
            Mark ${formatDecimal(displayMarkPrice, 4)}
          </span>
          {isSubscribed && (
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
            </span>
          )}
          <span>·</span>
          <span className={pnlColor(livePnl)}>
            {livePnl >= 0 ? "+" : ""}${formatUsd(livePnl)}
          </span>
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs text-text-secondary">
          Size {formatDecimal(position.positionAmount, 4)}
        </p>
      </div>
    </div>
  );
};

const HistoryPositionRow: React.FC<{ position: CopyTradeLeaderPosition }> = ({
  position,
}) => (
  <div className="bg-primary/30 rounded-xl p-3 border border-border/50 flex items-center justify-between gap-3">
    <div className="min-w-0 space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-text-primary">
          {position.symbol}
        </span>
        <SideBadge side={position.side} />
        <span className="text-xs text-text-secondary">
          {position.leverage}x
        </span>
        <span className="text-xs text-text-secondary">{position.status}</span>
      </div>
      <p className="text-xs text-text-secondary">
        Opened {formatDateTime(position.opened)}
        {position.closed != null
          ? ` · Closed ${formatDateTime(position.closed)}`
          : ""}
      </p>
    </div>
    <div className="text-right shrink-0">
      {isFiniteNumber(position.closingPnl) ? (
        <p className={`text-sm font-medium ${pnlColor(position.closingPnl)}`}>
          {position.closingPnl >= 0 ? "+" : ""}
          ${formatUsd(position.closingPnl)}
        </p>
      ) : (
        <p className="text-sm font-medium text-text-secondary">—</p>
      )}
      {isFiniteNumber(position.roi) && (
        <p className={`text-xs ${pnlColor(position.roi)}`}>
          {formatPercent(position.roi)}
        </p>
      )}
    </div>
  </div>
);

const Stat: React.FC<{
  label: string;
  value: string;
  tooltip?: string;
}> = ({ label, value, tooltip }) => (
  <div className="bg-primary/30 rounded-xl p-3 border border-border/50">
    <div className="flex items-center gap-1 mb-1">
      <p className="text-xs text-text-secondary">{label}</p>
      {tooltip && <MetricTooltip text={tooltip} />}
    </div>
    <p className="text-sm font-medium text-text-primary">{value}</p>
  </div>
);

const MetricTooltip: React.FC<{ text: string }> = ({ text }) => (
  <HelpTooltip text={text}>
    <HelpCircle
      className="w-3.5 h-3.5 text-text-secondary/70 cursor-help shrink-0"
      aria-label="More info"
      tabIndex={0}
    />
  </HelpTooltip>
);
